import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField
} from '@mui/material';
import { useLoading } from '../../hooks/useLoading';
import { useNotifications } from '../../hooks/useNotifications';
import { createDocumentRepositoryFolder } from '../../constants/requests';

/**
 * Dialog to create a new folder in the current location.
 * Submit: POST create folder with repositoryId, creatorId, name, folderId (null for root).
 * On success: close, show notification, call onSuccess (e.g. refresh folder list).
 */
export default function CreateFolderDialog({
  open,
  onClose,
  repositoryId,
  currentFolderId,
  creatorId,
  employeeId,
  onSuccess
}) {
  const { request } = useLoading();
  const { showNotifications } = useNotifications();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name?.trim() ?? '';
    if (!trimmed) {
      showNotifications('warning', 'Folder name is required', 3000);
      return;
    }
    if (!repositoryId || !creatorId || !employeeId) {
      showNotifications('error', 'Missing repository or user context', 3000);
      return;
    }

    setSubmitting(true);
    try {
      const response = await request(() =>
        createDocumentRepositoryFolder(
          repositoryId,
          {
            creatorId,
            name: trimmed,
            folderId: currentFolderId ?? null
          },
          employeeId
        )
      );

      if (response?.data?.success && response?.data?.data) {
        showNotifications('success', 'Folder created successfully', 3000);
        onClose();
        onSuccess?.();
      } else {
        showNotifications(
          'error',
          response?.data?.message ?? 'Failed to create folder',
          5000
        );
      }
    } catch (err) {
      showNotifications(
        'error',
        err?.response?.data?.message ??
          err?.message ??
          'Failed to create folder',
        5000
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>New folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder name"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
            inputProps={{ maxLength: 255 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
